/**
 * @fileoverview API service and React Query hooks for Baselinker product import.
 * @module features/baselinker-import/api/baselinkerImportService
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import apiClient from '@/lib/apiClient'
import {
  BaselinkerInventoryArraySchema,
  ImportPreviewResponseSchema,
  BaselinkerImportResponseSchema,
  type BaselinkerInventory,
  type ImportPreviewResponse,
  type BaselinkerImportRequest,
  type BaselinkerImportResponse,
} from '../schemas/baselinkerImportSchema'

export const BASELINKER_INVENTORIES_QUERY_KEY = ['baselinker', 'inventories'] as const
export const BASELINKER_IMPORT_PREVIEW_QUERY_KEY = ['baselinker', 'import-preview'] as const

/**
 * Fetch available Baselinker inventories/catalogs.
 * @returns Array of inventories
 */
export async function getBaselinkerInventories(): Promise<BaselinkerInventory[]> {
  const res = await apiClient.get('/baselinker/inventories')
  return BaselinkerInventoryArraySchema.parse(res.data)
}

/**
 * Fetch import preview for a specific inventory.
 * @param inventoryId Baselinker inventory ID
 * @returns Preview of products to import
 */
export async function getImportPreview(inventoryId: number): Promise<ImportPreviewResponse> {
  const res = await apiClient.get('/baselinker/products/preview', {
    params: { inventoryId },
  })
  return ImportPreviewResponseSchema.parse(res.data)
}

/**
 * Execute product import from Baselinker.
 * @param request Import request with inventory ID and optional product IDs
 * @returns Import result summary
 */
export async function importBaselinkerProducts(
  request: BaselinkerImportRequest
): Promise<BaselinkerImportResponse> {
  const res = await apiClient.post('/baselinker/products/import', request)
  return BaselinkerImportResponseSchema.parse(res.data)
}

/**
 * React Query hook for fetching Baselinker inventories.
 * @param enabled Whether the query should be enabled
 * @returns Query result with inventories
 */
export function useBaselinkerInventoriesQuery(
  enabled = true
): UseQueryResult<BaselinkerInventory[], Error> {
  return useQuery<BaselinkerInventory[], Error>({
    queryKey: BASELINKER_INVENTORIES_QUERY_KEY,
    queryFn: getBaselinkerInventories,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * React Query hook for fetching import preview.
 * @param inventoryId Baselinker inventory ID
 * @param enabled Whether the query should be enabled
 * @returns Query result with import preview
 */
export function useImportPreviewQuery(
  inventoryId: number | null,
  enabled = true
): UseQueryResult<ImportPreviewResponse, Error> {
  return useQuery<ImportPreviewResponse, Error>({
    queryKey: [...BASELINKER_IMPORT_PREVIEW_QUERY_KEY, inventoryId],
    queryFn: () => getImportPreview(inventoryId!),
    enabled: enabled && inventoryId !== null,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * React Query mutation hook for importing products.
 * @returns Mutation result for product import
 */
export function useImportProductsMutation(): UseMutationResult<
  BaselinkerImportResponse,
  Error,
  BaselinkerImportRequest
> {
  const queryClient = useQueryClient()

  return useMutation<BaselinkerImportResponse, Error, BaselinkerImportRequest>({
    mutationFn: importBaselinkerProducts,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['products'] }),
        queryClient.invalidateQueries({ queryKey: ['variants'] }),
        queryClient.invalidateQueries({ queryKey: BASELINKER_IMPORT_PREVIEW_QUERY_KEY }),
      ])
    },
  })
}
